/**
 * ai-credits.ts
 * Zod contracts for the per-company AI credit wallet (kernel, 2026-10-12).
 *
 * Every AI call in the suite charges the company a fixed number of credits per
 * feature (AI_FEATURES). Each company gets `monthlyCredits` included; past it
 * every extra credit is billed at `overagePriceUsd`. The wallet NEVER blocks a
 * feature (user decision 2026-10-12): the quota is a clear reference, not a
 * restriction. Tokens and the provider cost in USD are recorded per call but
 * only the platform owner sees them. Modelled on Personio's AI credit wallet.
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 */
import { z } from 'zod';

/** What each AI feature costs the company, in credits. */
export const AI_FEATURES = {
  'servicios.voice_intake': { credits: 1, label: 'LOCX — carga por voz' },
  'rrhh.cv_profile': { credits: 1, label: 'NOMI — perfil desde el CV' },
  'rrhh.criteria_suggestion': { credits: 1, label: 'NOMI — requisitos sugeridos' },
  'rrhh.candidate_comparison': { credits: 3, label: 'NOMI — comparación de candidatos' },
} as const satisfies Record<string, { credits: number; label: string }>;

export type AiFeatureKey = keyof typeof AI_FEATURES;
export const AI_FEATURE_KEYS = Object.keys(AI_FEATURES) as [AiFeatureKey, ...AiFeatureKey[]];

/** The terms a company gets until the platform owner sets its own. */
export const DEFAULT_AI_WALLET = {
  monthlyCredits: 200,
  overagePriceUsd: 0.1,
} as const satisfies AiWallet;

const CreditCountSchema = z.number().int('must be a whole number').min(0, 'must be >= 0').max(10_000_000);

export const AiWalletSchema = z.object({
  monthlyCredits: CreditCountSchema,
  overagePriceUsd: z.number().min(0, 'must be >= 0').max(999_999),
});
export type AiWallet = z.infer<typeof AiWalletSchema>;

/** GET /platform/companies/:id/ai-wallet. `isDefault` = no terms stored yet. */
export const AiWalletResponseSchema = AiWalletSchema.extend({
  companyId: z.string().uuid(),
  isDefault: z.boolean(),
});
export type AiWalletResponse = z.infer<typeof AiWalletResponseSchema>;

/** `YYYY-MM`, the billing month (Argentina time). */
export const AiUsageMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be YYYY-MM');

export const AiUsageFeatureRowSchema = z.object({
  feature: z.string(),
  events: z.number().int(),
  credits: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  costUsd: z.number(),
});
export type AiUsageFeatureRow = z.infer<typeof AiUsageFeatureRowSchema>;

/** One company's numbers for a month (shared by the summary and the overview). */
export const AiUsageTotalsSchema = z.object({
  creditsIncluded: z.number().int(),
  creditsUsed: z.number().int(),
  overageCredits: z.number().int(),
  /** What to bill: overageCredits × overagePriceUsd. */
  overageUsd: z.number(),
  /** What the providers cost RigX. */
  costUsd: z.number(),
});
export type AiUsageTotals = z.infer<typeof AiUsageTotalsSchema>;

/** GET /platform/companies/:id/ai-usage?month=YYYY-MM */
export const AiUsageSummarySchema = AiUsageTotalsSchema.extend({
  companyId: z.string().uuid(),
  month: AiUsageMonthSchema,
  wallet: AiWalletSchema,
  byFeature: z.array(AiUsageFeatureRowSchema),
});
export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;

/** GET /platform/ai-usage?month=YYYY-MM — every company, for billing. */
export const AiUsageOverviewSchema = z.object({
  month: AiUsageMonthSchema,
  companies: z.array(
    AiUsageTotalsSchema.extend({
      companyId: z.string().uuid(),
      companyName: z.string(),
    }),
  ),
});
export type AiUsageOverview = z.infer<typeof AiUsageOverviewSchema>;

/**
 * GET /admin/ai-usage — the company's own view, "IA incluida en tu plan"
 * (2026-10-12). Speaks in uses (credits), never money nor tokens: the people
 * who click the AI buttons must not fear a surprise bill; the admin who knows
 * the commercial terms sees what was used, by what and by whom.
 */
export const CompanyAiUsageSchema = z.object({
  month: AiUsageMonthSchema,
  /** Uses included per month in the plan. */
  included: z.number().int(),
  used: z.number().int(),
  /** Uses past the included ones this month (0 when within the plan). */
  extra: z.number().int(),
  byFeature: z.array(z.object({ feature: z.string(), uses: z.number().int() })),
  byUser: z.array(z.object({ userId: z.string().uuid().nullable(), name: z.string(), uses: z.number().int() })),
});
export type CompanyAiUsage = z.infer<typeof CompanyAiUsageSchema>;
